import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

interface TimelineStep {
    title: string;
    description: string;
    callout?: {
        type: 'warning' | 'success';
        title: string;
        text: string;
    };
}

const steps: TimelineStep[] = [
    {
        title: '1. Contato Inicial',
        description: 'Você nos conta a situação da sua embarcação — compra, venda, renovação, regularização. Analisamos a documentação existente e identificamos o que precisa ser feito.',
        callout: {
            type: 'warning',
            title: 'Atenção ao Prazo',
            text: 'Em caso de compra/venda, o prazo para transferência é de 15 a 60 dias. Não perca o prazo e evite multas!',
        },
    },
    {
        title: '2. Levantamento Documental',
        description: 'Reunimos toda a documentação necessária: RG, CPF, comprovante de residência, nota fiscal da embarcação, TIE/TIEM original, fotos e comprovantes de propriedade do motor.',
    },
    {
        title: '3. Protocolo na Capitania',
        description: 'Preenchemos os formulários exigidos (BADE/BSADE), pagamos a GRU (taxa da Marinha), providenciamos reconhecimento de firmas e protocolamos tudo na Capitania dos Portos.',
    },
    {
        title: '4. Documento em Mãos',
        description: 'Após o prazo de análise da Capitania (10 a 30 dias úteis, conforme o serviço), retiramos o documento final — TIE, TIEM ou CHA — e entregamos a você.',
        callout: {
            type: 'success',
            title: 'Resultado',
            text: 'Embarcação 100% regularizada. É só navegar com tranquilidade!',
        },
    },
];

const stepGradients = [
    'from-gold to-gold/80',
    'from-gold/80 to-primary/70',
    'from-primary/70 to-primary/90',
    'from-primary/90 to-primary',
];

const JornadaEscalaSection: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
            { threshold: 0.15 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="py-24 sm:py-32 bg-primary/[0.03] relative overflow-hidden">
            {/* Background orb */}
            <div
                className="absolute left-1/2 -translate-x-1/2 top-0 w-[600px] h-[600px] pointer-events-none"
                style={{
                    background: 'radial-gradient(circle, hsl(222 47% 25% / 0.06), transparent 65%)',
                    filter: 'blur(50px)',
                }}
            />

            <div className="container mx-auto px-6 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                        Processo
                    </span>
                    <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary">
                        Como Funciona o Processo
                    </h2>
                    <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
                        Da consulta inicial à entrega do documento final, cuidamos de todos os trâmites para que você não precise se preocupar com burocracia.
                    </p>
                </motion.div>

                <div className="relative max-w-5xl mx-auto">
                    {/* Vertical Line (animated) */}
                    <motion.div
                        initial={{ height: 0 }}
                        animate={isVisible ? { height: '100%' } : { height: 0 }}
                        transition={{ duration: 1.5, ease: 'easeInOut' }}
                        className="hidden md:block absolute left-1/2 top-0 w-0.5 bg-gradient-to-b from-gold via-primary/40 to-primary -translate-x-1/2 origin-top"
                    />

                    {steps.map((step, index) => {
                        const isEven = index % 2 === 0;

                        return (
                            <div
                                key={index}
                                className="relative mb-16 last:mb-0"
                            >
                                <div className={`flex flex-col md:flex-row items-center ${isEven ? '' : 'md:flex-row-reverse'}`}>
                                    {/* Content Side */}
                                    <motion.div
                                        initial={{ opacity: 0, x: isEven ? -40 : 40 }}
                                        animate={isVisible ? { opacity: 1, x: 0 } : {}}
                                        transition={{ duration: 0.6, delay: 0.3 + index * 0.2 }}
                                        className={`md:w-5/12 ${isEven ? 'md:pr-10 md:text-right' : 'md:pl-10 md:text-left'}`}
                                    >
                                        <h4 className="text-xl font-bold text-primary mb-2">{step.title}</h4>
                                        <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                                    </motion.div>

                                    {/* Center Circle */}
                                    <motion.div
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={isVisible ? { scale: 1, opacity: 1 } : {}}
                                        transition={{ duration: 0.5, delay: 0.2 + index * 0.2, type: 'spring', stiffness: 300 }}
                                        className="relative my-6 md:my-0 z-10"
                                    >
                                        <div
                                            className={`w-14 h-14 rounded-full bg-gradient-to-br ${stepGradients[index]} flex items-center justify-center text-primary-foreground text-xl font-bold shadow-lg border-4 border-white`}
                                            style={{ boxShadow: '0 4px 20px hsl(43 74% 53% / 0.3)' }}
                                        >
                                            {index + 1}
                                        </div>
                                    </motion.div>

                                    {/* Callout Side or empty */}
                                    <div className={`md:w-5/12 ${isEven ? 'md:pl-10' : 'md:pr-10'}`}>
                                        {step.callout && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 15 }}
                                                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                                                transition={{ duration: 0.5, delay: 0.5 + index * 0.2 }}
                                                whileHover={{ scale: 1.03 }}
                                                className={`bg-white p-4 rounded-lg border-l-4 cursor-default ${step.callout.type === 'warning'
                                                    ? 'border-l-gold'
                                                    : 'border-l-green-500'
                                                    }`}
                                                style={{ boxShadow: '0 2px 12px hsl(0 0% 0% / 0.06)' }}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    {step.callout.type === 'warning' ? (
                                                        <AlertTriangle className="w-4 h-4 text-gold" />
                                                    ) : (
                                                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                                                    )}
                                                    <span className={`text-xs font-bold uppercase tracking-wider ${step.callout.type === 'warning' ? 'text-gold' : 'text-green-600'
                                                        }`}>
                                                        {step.callout.title}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-muted-foreground">{step.callout.text}</p>
                                            </motion.div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default JornadaEscalaSection;
